import { useState } from 'react';
import { useQuery } from 'wasp/client/operations';
import { getTopics, getTopicTypes } from 'wasp/client/operations';
import TopicCard from './TopicCard';

export default function TopicListPage() {
  const [selectedTypeKey, setSelectedTypeKey] = useState<string | null>(null);
  const [showOnlyRootTopics, setShowOnlyRootTopics] = useState(true);

  // Fetch topic types
  const { data: topicTypes, isLoading: typesLoading } = useQuery(getTopicTypes) as any;

  // Fetch topics with filters
  const {
    data: topics,
    isLoading: topicsLoading,
    error: topicsError,
  } = useQuery(getTopics, {
    isActive: true,
    ...(selectedTypeKey && { typeKey: selectedTypeKey }),
    ...(showOnlyRootTopics && { parentId: null }),
  }) as any;

  const isLoading = typesLoading || topicsLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading topics...</p>
        </div>
      </div>
    );
  }

  if (topicsError) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center text-red-600 dark:text-red-400">
          <p>Error loading topics</p>
          <p className="text-sm mt-2">{topicsError.message}</p>
        </div>
      </div>
    );
  }

  const filteredTopics = topics || [];

  return (
    <div className="w-full py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Community Topics
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Explore guidance and resources organized by topic
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Topic Type Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Type
              </label>
              <select
                value={selectedTypeKey || ''}
                onChange={(e) => setSelectedTypeKey(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Types</option>
                {topicTypes?.map((type: any) => (
                  <option key={type.id} value={type.key}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Hierarchy Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                View
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowOnlyRootTopics(true)}
                  className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                    showOnlyRootTopics
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Main Topics
                </button>
                <button
                  onClick={() => setShowOnlyRootTopics(false)}
                  className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                    !showOnlyRootTopics
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  All Topics
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {filteredTopics.length} topic{filteredTopics.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {/* Topics Grid */}
        {filteredTopics.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <div className="text-gray-400 dark:text-gray-500 mb-4">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No topics found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              There are no topics matching your filters.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              To add topics, run: <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">wasp db seed</code>
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTopics.map((topic: any) => (
              <TopicCard key={topic.id} topic={topic} />
            ))}
          </div>
        )}
      </div>
  );
}



